import { TooltipProps } from 'recharts';
import { Button, Stack, Typography } from '@mui/material';
import { format } from 'date-fns';
import { EnumCurrency } from 'tdc-web-backend/enums/enums';
import { useTranslation } from 'react-i18next';
import { enUS, hr } from 'date-fns/locale';
import { IGraphMilestone } from '../../interface';
import { formatCurrency } from '../../../../../../utils/helpers';

const ChartTooltip = ({ payload, label, active }: TooltipProps<number, string>) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'hr' ? hr : enUS;

  if (payload && active && payload.length) {
    const date = new Date(label);
    const payloadItem = payload[0].payload as IGraphMilestone;

    return (
      <Button sx={{ py: 2, opacity: 1 }} size="small" variant="contained">
        <Stack spacing={3} alignItems="start" justifyContent="space-between">
          {/* current date */}
          <Stack spacing={1} alignItems="start">
            <Typography component="span" fontWeight="600" color="grey.300">
              {format(date, 'MMM d, yyyy', { locale })}
            </Typography>
          </Stack>

          {/* paid milestones */}
          {payloadItem.paidMilestonesEur.length > 0 && (
            <Stack alignItems="start">
              <Stack alignItems="start">
                <Typography component="span" variant="bodyMedium2" fontWeight="600">
                  {payloadItem.paidMilestonesEur.length > 1
                    ? t('chart.paidMilestones')
                    : t('chart.paidMilestone')}{' '}
                  (€):
                </Typography>

                <Stack>
                  {payloadItem.paidMilestonesEur.map((name) => (
                    <Typography key={name}> {name}</Typography>
                  ))}
                </Stack>
              </Stack>

              {payloadItem.paidEur !== 0 && (
                <Typography>
                  {`${t('chart.payment')}: `}{' '}
                  <Typography component="span" variant="bodySemiBold2">
                    {formatCurrency(payloadItem.paidEur, EnumCurrency.Eur)}
                  </Typography>
                </Typography>
              )}
            </Stack>
          )}

          {payloadItem.paidMilestonesUsd.length > 0 && (
            <Stack alignItems="start" sx={{ pt: payloadItem.paidMilestonesEur.length > 0 ? 2 : 0 }}>
              <Stack alignItems="start">
                <Typography component="span" fontWeight="600">
                  {payloadItem.paidMilestonesUsd.length > 1
                    ? t('chart.paidMilestones')
                    : t('chart.paidMilestone')}{' '}
                  ($):
                </Typography>

                <Stack alignItems="start">
                  {payloadItem.paidMilestonesUsd.map((name) => (
                    <Typography key={name}>{name}</Typography>
                  ))}
                </Stack>
              </Stack>

              {payloadItem.paidUsd !== 0 && (
                <Typography>
                  {`${t('chart.payment')}: `}{' '}
                  <Typography component="span" variant="bodySemiBold2">
                    {formatCurrency(payloadItem.paidUsd, EnumCurrency.Usd)}
                  </Typography>
                </Typography>
              )}
            </Stack>
          )}

          {/* unpaid milestones */}
          {payloadItem.unpaidMilestonesEur.length > 0 && (
            <Stack
              alignItems="start"
              sx={{
                pt:
                  payloadItem.paidMilestonesUsd.length > 0 ||
                  payloadItem.paidMilestonesEur.length > 0
                    ? 2
                    : 0,
              }}
            >
              <Stack alignItems="start">
                <Typography component="span" fontWeight="600" alignItems="start">
                  {payloadItem.unpaidMilestonesEur.length > 1
                    ? t('chart.unpaidMilestones')
                    : t('chart.unpaidMilestone')}{' '}
                  (€):
                </Typography>

                <Stack alignItems="start">
                  {payloadItem.unpaidMilestonesEur.map((name) => (
                    <Typography key={name}>{name}</Typography>
                  ))}
                </Stack>
              </Stack>

              {payloadItem.unpaidEur !== 0 && (
                <Typography>
                  {`${t('common.expectedPayment')}: `}{' '}
                  <Typography component="span" variant="bodySemiBold2">
                    {formatCurrency(payloadItem.unpaidEur, EnumCurrency.Eur)}
                  </Typography>
                </Typography>
              )}
            </Stack>
          )}
          {payloadItem.unpaidMilestonesUsd.length > 0 && (
            <Stack
              alignItems="start"
              sx={{
                pt:
                  payloadItem.unpaidMilestonesEur.length > 0 ||
                  payloadItem.unpaidMilestonesUsd.length > 0
                    ? 2
                    : 0,
              }}
            >
              <Stack alignItems="start">
                <Typography component="span" fontWeight="600" alignItems="start">
                  {payloadItem.unpaidMilestonesUsd.length > 1
                    ? t('chart.unpaidMilestones')
                    : t('chart.unpaidMilestone')}{' '}
                  ($):
                </Typography>

                <Stack alignItems="start">
                  {payloadItem.unpaidMilestonesUsd.map((name) => (
                    <Typography key={name}>{name}</Typography>
                  ))}
                </Stack>
              </Stack>

              {payloadItem.unpaidUsd !== 0 && (
                <Typography>
                  {`${t('common.expectedPayment')}: `}{' '}
                  <Typography component="span" variant="bodySemiBold2">
                    {formatCurrency(payloadItem.unpaidEur, EnumCurrency.Usd)}
                  </Typography>
                </Typography>
              )}
            </Stack>
          )}
        </Stack>
      </Button>
    );
  }

  return null;
};

export default ChartTooltip;
